// Shared pieces of the public job board: the row types, label maps, small
// formatters, and the evaluate button. Used by Board.tsx (the list) and
// BoardJob.tsx (the public detail page) — extracted so the detail page never
// copies list code and the two cannot drift.
//
// The sponsor-register badge that used to live here was removed entirely by
// user decision (2026-08-13) — the data layer (sponsor_register table + sync
// script) survives untouched, so the feature can return if it earns its way
// back.

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api';
import { friendlyError } from '../../lib/errorMessages';

export type RoleLevel = 'internship' | 'graduate' | 'academic';

// User decision (2026-08-14): the 类型 axis reads 实习/兼职 vs 全职. The
// stored values stay 'internship' / 'graduate' — graduate schemes ARE the
// full-time track on an early-career board, and part-time titles classify
// into the internship bucket (see job-taxonomy.mjs). 学术/科研 added
// 2026-08-15 (segment decision: the track where sponsorship actually
// happens for Chinese nationals).
export const ROLE_LABELS: Record<RoleLevel, string> = {
  internship: '实习 / 兼职',
  graduate: '全职',
  academic: '学术 / 科研',
};

export const INDUSTRY_LABELS: Record<string, string> = {
  finance: '金融', tech: '科技', consulting: '咨询', sales: '销售',
  marketing: '市场', research: '科研', engineering: '工程',
  healthcare: '医疗', education: '教育', legal: '法律',
  hr: '人力资源', operations: '运营',
};

export interface BoardItem {
  // node-pg returns int8 as a string; the id is a route token, never math.
  id: string;
  url: string;
  // The employer's own ATS link when the aggregator exposed it — preferred
  // over url by the detail page's apply button. Null for rows scanned before
  // the column existed, and wherever jobspy had nothing.
  url_direct?: string | null;
  title: string;
  company: string | null;
  city: string | null;
  job_type: string | null;
  role_level: string;
  industry: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_interval: string | null;
  salary_currency: string | null;
  source: string | null;
  posted_at: string | null;
  first_seen: string;
}

export interface BoardResponse {
  items: BoardItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface Facets {
  roleLevel: Record<string, number>;
  industry: Record<string, number>;
  total: number;
}

export interface BoardJobResponse {
  job: BoardItem;
  body: string | null;
}

export function useBoardJob(id: string | undefined) {
  return useQuery({
    queryKey: ['board-job', id],
    queryFn: () => apiGet<BoardJobResponse>(`/api/board/job?id=${encodeURIComponent(id || '')}`),
    staleTime: 5 * 60_000,
    enabled: !!id,
    retry: false,
  });
}

export function salaryText(i: BoardItem): string | null {
  if (!i.salary_min && !i.salary_max) return null;
  const cur = i.salary_currency === 'USD' ? '$' : i.salary_currency === 'EUR' ? '€' : '£';
  const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n)));
  const range = i.salary_min && i.salary_max && i.salary_min !== i.salary_max
    ? `${fmt(i.salary_min)}–${fmt(i.salary_max)}`
    : fmt(i.salary_max || i.salary_min || 0);
  // hourly vs yearly matters enormously for part-time work; never drop it.
  const unit = i.salary_interval === 'hourly' ? '/小时'
    : i.salary_interval === 'monthly' ? '/月'
    : i.salary_interval === 'daily' ? '/天' : '';
  return `${cur}${range}${unit}`;
}

/**
 * Un-squash jobspy's markdown before rendering a JD.
 *
 * The artifact classes, each verified on live rows:
 *   1. Setext-underline residue — "**About Us**\n------------------ We are…".
 *      The converter kept the HTML heading's underline but glued it to the
 *      next paragraph, so a wall of literal dashes renders mid-page. The run
 *      is also the strongest heading signal we get: whatever bold phrase sits
 *      right above it IS a section title, even when that phrase was itself
 *      glued to the end of the previous paragraph.
 *   2. A list that starts INLINE after a colon or after plain prose —
 *      "…role: * Assists in…", "…as follows * Support the…" — so the first
 *      bullet hides inside the intro paragraph while its siblings render
 *      as a list.
 *   3. A paragraph that OPENS with a short bold phrase — "**Description** The
 *      Assistant…" — a section label wearing emphasis markup.
 *   4. Sentence glue — "save money.We're building" — a period the converter
 *      dropped the space after. (Escaped dots like "4\.9" never match: the
 *      char before the dot is a backslash.)
 * Structural fixes only; everything else is left alone.
 */
export function prepareJdMarkdown(raw: string): string {
  const isLabel = (label: string) =>
    label.split(/\s+/).length <= 6 && !/[.!?。！？]$/.test(label.trim());

  let s = String(raw || '').replace(/\r/g, '').trim();

  // (1) Pull the bold label off the line above a dash run onto its own
  // block and drop the run; the block-start rule below promotes it.
  s = s.replace(/\*\*([^*\n]{2,48})\*\*[:：]?[ \t]*\n[-=_]{4,}[ \t]*/g, '\n\n**$1**\n\n');
  // Leftover runs with no label: glued to following text, or on their own
  // line (a would-be setext heading). The (?=[^|\s]) guard leaves GFM table
  // separator rows — which contain '|' — intact.
  s = s.replace(/\n[-=_]{4,}[ \t]+(?=[^|\s])/g, '\n\n');
  s = s.replace(/\n[-=_]{4,}[ \t]*\n/g, '\n\n');

  // (4) Sentence glue.
  s = s.replace(/([a-z])\.([A-Z][a-z])/g, '$1. $2');

  // (2) Inline bullets. The prose variant only fires when what follows looks
  // like list content (bold or a capital) — a lone mid-sentence "*" with
  // lowercase after it stays put.
  s = s.replace(/([:：])\s+\*\s+/g, '$1\n\n* ');
  s = s.replace(/([a-zA-Z).,])[ \t]+\*[ \t]+(?=\*\*|[A-Z])/g, '$1\n\n* ');

  // (3) A bold label buried MID-paragraph right after a sentence end — "…Join
  // us. **Job Description** AECOM is…" — is a squashed section boundary,
  // not emphasis. Split it out so the block-start rule below can see it.
  s = s.replace(/([.!?])\s+\*\*([^*\n]{2,48})\*\*[:：]?\s+/g, (whole, punct, label) =>
    isLabel(label) ? `${punct}\n\n**${label.trim()}** ` : whole);
  s = s
    .split(/\n{2,}/)
    .map((block) => {
      const m = block.match(/^\*\*([^*\n]{2,48})\*\*[:：]?\s*([\s\S]*)$/);
      if (!m) return block;
      // A colon inside the bold ("**About the role:**") shouldn't survive
      // into the heading text.
      const label = m[1].trim().replace(/[:：]+$/, '');
      // Labels are short noun phrases; a bolded full sentence stays bold.
      if (!isLabel(label)) return block;
      const rest = m[2].trim();
      return rest ? `### ${label}\n\n${rest}` : `### ${label}`;
    })
    .join('\n\n');
  return s.replace(/\n{3,}/g, '\n\n').trim();
}

export function daysAgo(iso: string | null): string {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (!Number.isFinite(d) || d < 0) return '';
  return d === 0 ? '今天' : d === 1 ? '昨天' : `${d} 天前`;
}

// jobspy stores the literal string "Unknown Company" as the company name when a
// posting has none, so it arrives as data rather than as an empty field. Mapped
// at display time — rewriting it in the scanner would change what the English
// product records too.
export function displayCompany(company: string): string {
  return company === 'Unknown Company' ? '公司未标注' : company;
}

// --- Content language (titles + JD default) -------------------------------
//
// Job titles AND the JD body default to Chinese, with an 原文 toggle (user
// decisions, 2026-08-14). One preference governs both surfaces — the board's
// 语言 chips and the JD card's chips are two placements of the same switch.
// Engine: the READER's own browser-built-in Translator API — on-device, free,
// feature-detected, so unsupported browsers simply see English and no toggle.
// Company names are deliberately NOT translated: they are brand identifiers
// with no canonical Chinese form.
//
// Module-level singletons: the list and the detail page share one translator
// instance and the finished-translation caches, so paging back and forth
// never re-translates anything.

type NativeTranslator = { translate(s: string): Promise<string> };

// Key kept from when the preference covered titles only — renaming it would
// silently reset every existing visitor's stored choice.
const TITLES_ZH_KEY = 'n2j_titles_zh';
const titleCache = new Map<string, string>();
/** Finished JD translations, keyed by the cleaned markdown (BoardJob). */
export const jdZhCache = new Map<string, string>();
let translatorP: Promise<NativeTranslator> | null = null;

function translatorGlobal() {
  return (globalThis as unknown as {
    Translator?: {
      availability(o: object): Promise<string>;
      create(o: object): Promise<NativeTranslator>;
    };
  }).Translator;
}

/** The shared en→zh translator (also used by the JD card in BoardJob). */
export function getZhTranslator(): Promise<NativeTranslator> {
  const T = translatorGlobal();
  if (!T) return Promise.reject(new Error('unsupported'));
  if (!translatorP) {
    translatorP = T.create({ sourceLanguage: 'en', targetLanguage: 'zh' });
    // A failed language-pack download must not poison every later attempt.
    translatorP.catch(() => { translatorP = null; });
  }
  return translatorP;
}

/**
 * The 中文/原文 preference plus what the browser can actually deliver.
 * `active` is what display code obeys; `supported` is whether to render the
 * toggle at all. NOTE: each call is an independent state instance — pages
 * hold ONE instance and pass it down (BoardJob → JdCard), so a toggle click
 * anywhere on the page moves every surface at once.
 */
export function useContentLang() {
  // 'ready': language pack on disk — translation can start with no gesture,
  // which is what makes default-to-Chinese possible. 'needs-click': the pack
  // must download first, and Chrome only allows that from a user gesture, so
  // the first 中文 click provides it and every later visit is 'ready'.
  const [avail, setAvail] = useState<'no' | 'ready' | 'needs-click'>('no');
  const [zh, setZhState] = useState(() => {
    try { return localStorage.getItem(TITLES_ZH_KEY) !== '0'; } catch { return true; }
  });
  useEffect(() => {
    const T = translatorGlobal();
    if (!T?.availability) return;
    T.availability({ sourceLanguage: 'en', targetLanguage: 'zh' })
      .then((a) => setAvail(a === 'available' ? 'ready' : a === 'unavailable' ? 'no' : 'needs-click'))
      .catch(() => { /* stay 'no' */ });
  }, []);
  const setZh = (v: boolean) => {
    setZhState(v);
    try { localStorage.setItem(TITLES_ZH_KEY, v ? '1' : '0'); } catch { /* private mode */ }
    if (v && avail === 'needs-click') {
      // Still inside the click that called us — the gesture the download needs.
      getZhTranslator().then(() => setAvail('ready')).catch(() => setAvail('no'));
    }
  };
  return { zh, setZh, supported: avail !== 'no', active: zh && avail === 'ready' };
}

/** One job title: original until its translation lands, then Chinese. */
export function JobTitle({ title, active, className, style }: {
  title: string; active: boolean; className?: string; style?: CSSProperties;
}) {
  const [, bump] = useState(0);
  useEffect(() => {
    if (!active || !title || titleCache.has(title)) return;
    let dead = false;
    getZhTranslator()
      .then((tr) => tr.translate(title))
      .then((t) => { if (!dead && t) { titleCache.set(title, t); bump((n) => n + 1); } })
      .catch(() => { /* leave the original */ });
    return () => { dead = true; };
  }, [title, active]);
  const shown = (active && titleCache.get(title)) || title;
  return <div className={className} style={style} lang={shown === title ? 'en' : 'zh'}>{shown}</div>;
}

/**
 * Queues one board job for evaluation against the signed-in user's own CV.
 * Uses the same /api/evaluate-bulk contract as the Matches paste-a-URL box, so
 * the result lands in the same place and CV Writer can pick it up.
 *
 * Deliberately one row at a time: inference is a small self-hosted pool, and a
 * select-all over the whole board would bury the queue for everyone.
 *
 * Signed-in callers only — the public list renders nothing in its place (the
 * user's requirement: anonymous rows carry job information and nothing else),
 * and the detail page's conversion card handles the anonymous pitch.
 */
export function AddToPipeline({ item }: { item: BoardItem }) {
  const [state, setState] = useState<'idle' | 'busy' | 'queued' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const add = async () => {
    setState('busy');
    try {
      await apiPost('/api/evaluate-bulk', {
        items: [{ url: item.url, company: item.company || '', role: item.title || '' }],
      });
      setState('queued');
    } catch (e) {
      setMsg(friendlyError(e, true));
      setState('error');
    }
  };

  if (state === 'queued') {
    // Matches the button's box, deliberately. The confirmation used to be a
    // bare .page-sub span (~22px) replacing a .btn (~35px), so every click
    // collapsed its row by ~13px and jerked the rest of the list upward —
    // right as the user was reading the next row down. Same padding, border
    // and font-size as .btn, just without the button chrome.
    return (
      <span
        className="page-sub"
        onClick={(e) => e.stopPropagation()}
        style={{
          whiteSpace: 'nowrap',
          color: 'var(--pri)',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '7px 12px',
          border: '1px solid transparent',
          fontSize: 12.5,
          lineHeight: 1.2,
        }}
      >
        ✓ 已加入评估
      </span>
    );
  }
  return (
    // stopPropagation on the wrapper, not the button: the whole span sits
    // inside a clickable .row, and the error text or the disabled button's
    // padding would otherwise still navigate to the detail page.
    <span
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}
    >
      <button
        className="btn"
        style={{ whiteSpace: 'nowrap' }}
        disabled={state === 'busy'}
        onClick={add}
        title="用你的简历给这个岗位打分，之后可以在「简历定制」里针对它定制简历"
      >
        {state === 'busy' ? '加入中…' : '评估这个岗位'}
      </button>
      {state === 'error' && (
        <span className="page-sub" style={{ color: '#b45309', maxWidth: 220, textAlign: 'right' }}>{msg}</span>
      )}
    </span>
  );
}
